import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { Connection } from "@solana/web3.js";
import { useMemo, useState } from "react";
import {
  BPS,
  FRONTEND_FEE_BPS,
  FRONTEND_FEE_RECIPIENT,
  RPC_FALLBACK,
} from "../lib/constants";
import { quoteBuy, quoteSell } from "../lib/curve";
import { formatTokenAmount, parseUnits } from "../lib/format";
import { buildSwapTransaction, confirmSwapTransaction } from "../lib/transactions";
import type { Market, TradeSide } from "../lib/types";
import { useBalances } from "../hooks/useBalances";

type TradeStatus =
  | { type: "idle" }
  | { type: "pending"; message: string }
  | { type: "success"; signature: string }
  | { type: "error"; message: string; signature?: string };

function rawToInput(raw: bigint, decimals: number): string {
  const scale = 10n ** BigInt(decimals);
  const whole = raw / scale;
  const fraction = (raw % scale).toString().padStart(decimals, "0").replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

function friendlyError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/User rejected|rejected the request|cancelled/i.test(message)) return "The wallet request was cancelled.";
  if (/insufficient funds|insufficient lamports/i.test(message)) return "Not enough SOL for this trade and network fees.";
  if (/slippage|minimum|custom program error: 0x1771/i.test(message)) return "The price moved beyond your slippage limit. Refresh and try again.";
  if (/status is still unknown/i.test(message)) return "The transaction was sent, but its status is still unknown. Check it on Solscan before trying again.";
  if (/block height exceeded|expired|confirmation timed out/i.test(message)) return "Confirmation is taking longer than expected. Check the signature on Solscan before trying again.";
  return message.length > 220 ? `${message.slice(0, 217)}…` : message;
}

export function TradePanel(props: {
  market: Market;
  onConfirmed: () => Promise<unknown> | void;
}) {
  const { market, onConfirmed } = props;
  const { connection } = useConnection();
  const { publicKey, connected, sendTransaction } = useWallet();
  const [side, setSide] = useState<TradeSide>("buy");
  const [amount, setAmount] = useState("");
  const [slippageBps, setSlippageBps] = useState(500);
  const [status, setStatus] = useState<TradeStatus>({ type: "idle" });
  const balances = useBalances(connection, publicKey, market);

  const parsed = useMemo(() => {
    try {
      const raw = parseUnits(amount || "0", side === "buy" ? 9 : market.decimals);
      return { raw };
    } catch (error) {
      return { raw: 0n, error: error instanceof Error ? error.message : "Invalid amount" };
    }
  }, [amount, market.decimals, side]);

  const quote = useMemo(
    () =>
      side === "buy"
        ? quoteBuy(market, parsed.raw, BigInt(slippageBps))
        : quoteSell(market, parsed.raw, BigInt(slippageBps)),
    [market, parsed.raw, side, slippageBps],
  );
  const isFeeRecipient = publicKey?.equals(FRONTEND_FEE_RECIPIENT) ?? false;
  const frontendFeeLamports =
    isFeeRecipient
      ? 0n
      : side === "buy"
      ? (parsed.raw * FRONTEND_FEE_BPS) / BPS
      : (quote.expectedOut * FRONTEND_FEE_BPS) / BPS;
  const netExpectedOut =
    side === "sell" ? quote.expectedOut - frontendFeeLamports : quote.expectedOut;
  const minimumFrontendFee =
    side === "sell" ? (quote.minOut * FRONTEND_FEE_BPS) / BPS : 0n;
  const netMinimumOut =
    side === "sell" ? quote.minOut - minimumFrontendFee : quote.minOut;
  const buyTotalLamports = parsed.raw + frontendFeeLamports;

  const insufficient =
    side === "buy"
      ? buyTotalLamports + 4_000_000n > balances.solLamports
      : parsed.raw > balances.tokenRaw;
  const busy = status.type === "pending";
  const canTrade =
    connected &&
    parsed.raw > 0n &&
    quote.expectedOut > 0n &&
    !parsed.error &&
    !insufficient &&
    !busy;

  const outputLabel =
    side === "buy"
      ? formatTokenAmount(netExpectedOut, market.decimals, 6)
      : formatTokenAmount(netExpectedOut, 9, 7);
  const minimumLabel =
    side === "buy"
      ? `${formatTokenAmount(netMinimumOut, market.decimals, 6)} ${market.symbol}`
      : `${formatTokenAmount(netMinimumOut, 9, 7)} SOL`;
  const ammFeeLabel = `${formatTokenAmount(quote.feeAmount, 9, 7)} SOL`;
  const frontendFeeLabel = `${formatTokenAmount(frontendFeeLamports, 9, 7)} SOL`;

  function changeSide(next: TradeSide) {
    setSide(next);
    setAmount("");
    setStatus({ type: "idle" });
  }

  function useMaximum() {
    if (side === "buy") {
      const reserve = 4_000_000n;
      const available = balances.solLamports > reserve ? balances.solLamports - reserve : 0n;
      const maximum = (available * BPS) / (BPS + FRONTEND_FEE_BPS);
      setAmount(rawToInput(maximum, 9));
    } else {
      setAmount(rawToInput(balances.tokenRaw, market.decimals));
    }
  }

  async function executeTrade() {
    if (!publicKey || !canTrade) return;
    let submittedSignature: string | undefined;
    setStatus({ type: "pending", message: "Preparing trade…" });
    try {
      const { transaction } = buildSwapTransaction({
        user: publicKey,
        market,
        amount: parsed.raw,
        limit: quote.minOut,
        side,
      });
      const latest = await connection.getLatestBlockhash("processed");
      transaction.feePayer = publicKey;
      transaction.recentBlockhash = latest.blockhash;
      setStatus({ type: "pending", message: "Confirm in your wallet…" });
      const signature = await sendTransaction(transaction, connection, {
        skipPreflight: false,
        maxRetries: 10,
        preflightCommitment: "processed",
      });
      submittedSignature = signature;
      setStatus({ type: "pending", message: "Confirming on-chain…" });
      await confirmSwapTransaction({
        connection,
        fallbackConnection:
          RPC_FALLBACK && RPC_FALLBACK !== connection.rpcEndpoint
            ? new Connection(RPC_FALLBACK, "confirmed")
            : undefined,
        signature,
      });
      setStatus({ type: "success", signature });
      setAmount("");
      await Promise.all([balances.refresh(), onConfirmed()]);
    } catch (error) {
      setStatus({
        type: "error",
        message: friendlyError(error),
        signature: submittedSignature,
      });
    }
  }

  return (
    <aside className="trade-panel panel">
      <div className="trade-tabs">
        <button className={side === "buy" ? "active buy" : ""} onClick={() => changeSide("buy")}>Buy</button>
        <button className={side === "sell" ? "active sell" : ""} onClick={() => changeSide("sell")}>Sell</button>
      </div>

      <div className="trade-balance">
        <span>Balance</span>
        <button onClick={useMaximum} disabled={!connected || balances.loading}>
          {side === "buy"
            ? `${formatTokenAmount(balances.solLamports, 9, 5)} SOL`
            : `${formatTokenAmount(balances.tokenRaw, market.decimals, 4)} ${market.symbol}`}
        </button>
      </div>

      <div className="amount-box">
        <label>You pay</label>
        <div>
          <input
            inputMode="decimal"
            autoComplete="off"
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value.replace(/[^\d.,]/g, ""));
              setStatus({ type: "idle" });
            }}
            placeholder="0.00"
          aria-label="Trade amount"
          />
          <strong>{side === "buy" ? "SOL" : market.symbol}</strong>
        </div>
      </div>

      <div className="swap-arrow" aria-hidden="true">↓</div>

      <div className="amount-box output">
        <label>You receive</label>
        <div><output>{outputLabel}</output><strong>{side === "buy" ? market.symbol : "SOL"}</strong></div>
      </div>

      <div className="quote-summary">
        <div><span>Price impact</span><strong className={quote.priceImpactPct >= 5 ? "danger" : ""}>{quote.priceImpactPct.toFixed(2)}%</strong></div>
        <div><span>Minimum received</span><strong>{minimumLabel}</strong></div>
      </div>

      <details className="trade-settings">
        <summary>Trade settings</summary>
        <div className="slippage-row">
          <span>Max slippage</span>
          <div>
            {[100, 300, 500].map((bps) => (
              <button key={bps} className={slippageBps === bps ? "active" : ""} onClick={() => setSlippageBps(bps)}>{bps / 100}%</button>
            ))}
          </div>
        </div>
        <div className="quote-details">
          <div><span>Tribe AMM fee</span><strong>{(market.feeBps / 100).toFixed(2)}% · {ammFeeLabel}</strong></div>
          <div><span>Interface fee</span><strong>{isFeeRecipient ? "Fee wallet" : "1.00%"} · {frontendFeeLabel}</strong></div>
          {side === "buy" && (
            <div><span>Total SOL debit</span><strong>{formatTokenAmount(buyTotalLamports, 9, 7)} SOL</strong></div>
          )}
          <div><span>Total impact</span><strong>{quote.totalImpactPct.toFixed(2)}%</strong></div>
        </div>
      </details>

      {quote.priceImpactPct >= 5 && parsed.raw > 0n && (
        <div className="trade-warning">High price impact. Try a smaller trade.</div>
      )}
      {parsed.error && amount && <div className="field-error">{parsed.error}</div>}
      {insufficient && parsed.raw > 0n && <div className="field-error">Insufficient balance.</div>}

      {!connected ? (
        <WalletMultiButton className="trade-action" />
      ) : (
        <button className={`trade-action ${side}`} disabled={!canTrade} onClick={() => void executeTrade()}>
          {busy ? status.message : side === "buy" ? `Buy ${market.symbol}` : `Sell ${market.symbol}`}
        </button>
      )}

      {status.type === "success" && (
        <div className="transaction-result success">
          Trade confirmed. <a href={`https://solscan.io/tx/${status.signature}`} target="_blank" rel="noreferrer">View on Solscan ↗</a>
        </div>
      )}
      {status.type === "error" && (
        <div className="transaction-result error">
          {status.message}{" "}
          {status.signature && (
            <a href={`https://solscan.io/tx/${status.signature}`} target="_blank" rel="noreferrer">
              Check transaction ↗
            </a>
          )}
        </div>
      )}

      <p className="trade-disclaimer">
        {isFeeRecipient
          ? "This wallet receives interface fees, so it pays no net interface fee."
          : "Trades include a 1% interface fee, enforced atomically on-chain."}{" "}
        Private keys never enter this app.
      </p>
    </aside>
  );
}
